import { useCallback, useMemo } from 'react';
import { UsePaginationAndSortingProps } from '../types';
import { PAGE_SIZES } from '../constants';

export const usePaginationAndSorting = ({
  isAuthenticated,
  isTrial,
  restrictBrowseAll,
  limit,
  orderByParam,
  order,
  setSkip,
  setLimit,
  setOrderByParam,
  setOrder,
}: UsePaginationAndSortingProps) => {
  const getPageSizes = useCallback(() => {
    if (!isAuthenticated) return PAGE_SIZES.UNAUTHENTICATED;
    if (isTrial) return PAGE_SIZES.TRIAL;
    if (restrictBrowseAll) return PAGE_SIZES.RESTRICTED;
    return PAGE_SIZES.FULL;
  }, [isAuthenticated, isTrial, restrictBrowseAll]);

  const memoizedPageSizes = useMemo(() => getPageSizes(), [getPageSizes]);

  const memoizedSorting = useMemo(
    () => (orderByParam ? [{ id: orderByParam, desc: order === -1 }] : []),
    [orderByParam, order]
  );

  const handlePageChange = useCallback(
    (newPageIndex: number) => setSkip(newPageIndex * limit),
    [limit, setSkip]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setLimit(newPageSize);
      setSkip(0);
    },
    [setLimit, setSkip]
  );

  const handleSortingChange = useCallback(
    (updater: any) => {
      const newSorting =
        typeof updater === 'function'
          ? updater(orderByParam ? [{ id: orderByParam, desc: order === -1 }] : [])
          : updater;

      if (newSorting.length > 0) {
        setOrderByParam(newSorting[0].id);
        setOrder(newSorting[0].desc ? -1 : 1);
      } else {
        setOrderByParam(undefined);
        setOrder(undefined);
      }
    },
    [orderByParam, order, setOrderByParam, setOrder]
  );

  return {
    memoizedPageSizes,
    memoizedSorting,
    handlePageChange,
    handlePageSizeChange,
    handleSortingChange,
  };
};